import { apiFetch } from './http';

/**
 * One night as `GET /api/sessions` lists it: a platform session (with the
 * panel's match report when one is linked) or a match played on the panel
 * that no session claims (`kind === 'panel'`, no `code`).
 */
export interface SessionRow {
  /** `orphan`: a match of one of the platform's own missions whose session (or mission) is gone. */
  kind: 'platform' | 'panel' | 'orphan';
  code: string | null;
  missionCode: string | null;
  /** The mission's revision as it stands, which with its code is the address of its cover. */
  missionRevision?: number | null;
  missionTitle: string;
  worldName: string | null;
  /** The world's readable name; the class name when the platform has nothing better. */
  worldDisplayName?: string | null;
  state: 'SCHEDULED' | 'LIVE' | 'ENDED';
  scheduledAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  outcome: string | null;
  serverName: string | null;
  playerCount: number;
  /** Who is on the server right now, from the panel; null when it cannot say or the night is over. */
  playersOnline?: number | null;
  /** Null while the platform does not know it yet. */
  durationSeconds: number | null;
  /** This account took part, by the Steam identity on the match's roster. */
  played: boolean;
  reportId: number | null;
  reportUrl: string | null;
  pdfUrl: string | null;
  /** The mission's type and the picture it is drawn with; all null on a panel-only match. */
  recipe?: string | null;
  recipeTitle?: string | null;
  recipeIcon?: string | null;
  cover?: 'AUTO' | 'TERRAIN' | 'TYPE' | null;
  /** A smoke night: the platform playing itself to check a build. The list hides it unless asked. */
  smoke: boolean;
  /** A test of any kind (a smoke night, an inspection tour, a test card's session): only an admin who asked gets one. */
  test?: boolean;
  /**
   * The panel's own name of the mission, on a row made from one of its matches
   * alone: a file name (`ops_2xqvgqb`) for a mission the platform published.
   * `missionTitle` is what is read; this is the hint beside it.
   */
  missionFile?: string | null;
}

export interface SessionsList {
  rows: SessionRow[];
  /** Admins see every ended row; members only those they played. */
  canSeeAll: boolean;
  /** How many tests the answer left out, for an admin's switch to say; 0 for anybody else. */
  hiddenTests?: number;
}

/** `tests` brings the tests back, for an admin; anybody else never gets one, asked or not. */
export const fetchSessions = (tests = false) =>
  apiFetch<SessionsList>(tests ? '/api/sessions?tests=1' : '/api/sessions');

/**
 * Removes a night for good (an admin, or the mission's own author). The
 * panel's own matches are not ours to remove: a row with no `code` comes from
 * the panel and is deleted on the reports page, which asks the panel.
 */
export const deleteSession = (code: string) =>
  apiFetch<null>(`/api/sessions/${code}`, { method: 'DELETE' });

/** One person at a night that is over: a name and a picture, never a Steam id and never a headless client. */
export interface Attendee {
  name: string;
  /** The account's handle when the person has one: the way to their profile. */
  handle: string | null;
  avatarUrl: string | null;
}

/** Who was there, for members; empty before the night is over. */
export const fetchSessionAttendees = (code: string) =>
  apiFetch<Attendee[]>(`/api/sessions/${code}/attendees`);
